// src/components/admin/OrganisationAdminPanel.js
/**
 * OrganisationAdminPanel
 * ----------------------
 * Minimal admin tooling backed by the organisation-service (DASH-AUTH-2/3).
 *
 *  • Super users (bootstrap admins) can create organisations and invite a user
 *    to any organisation (the organisation id field is shown to them).
 *  • Organisation managers can invite a user to their own organisation (the
 *    organisation id is implied by their membership and not shown).
 *
 * Authorisation is enforced by the backend; this UI gates visibility off the
 * `isBootstrapAdmin` flag and the caller's resolved dashboard permission
 * context.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import Modal from "../Modal";
import { renderNotice } from "../Notice";
import {
  createOrganisation,
  fetchOrganisations,
  updateOrganisationProfile,
} from "../../api/organisations";
import {
  createInvite,
  fetchInvites,
  updateInvite,
  updateMembershipRole,
} from "../../api/invites";
import { canManageDashboardOrganisation } from "../../utils/authentication/dashboardPermissions";

const TRANSIENT_NOTICE_MS = 15000;

const ROLE_OPTIONS = [
  { value: "clinician", key: "admin.roles.clinician", fallback: "Clinician" },
  { value: "admin", key: "admin.roles.admin", fallback: "Admin" },
  {
    value: "researcher",
    key: "admin.roles.researcher",
    fallback: "Researcher",
  },
  { value: "operator", key: "admin.roles.operator", fallback: "Operator" },
];

const ORG_PRODUCT_FEATURE_SECTIONS = [
  {
    surfaceKey: "userWebApp",
    titleKey: "admin.createOrg.featureSurfaces.userWebApp",
    titleFallback: "User web app",
    sections: [
      {
        sectionKey: "modes",
        titleKey: "admin.createOrg.featureSections.modes",
        titleFallback: "Modes",
        features: [
          {
            featureKey: "spirometry",
            labelKey: "admin.createOrg.featureLabels.spirometry",
            labelFallback: "Spirometry",
          },
          {
            featureKey: "cardiopulmonary",
            labelKey: "admin.createOrg.featureLabels.cardiopulmonary",
            labelFallback: "Vitals/Cardiopulmonary",
          },
          {
            featureKey: "pulse",
            labelKey: "admin.createOrg.featureLabels.pulse",
            labelFallback: "Pulse",
          },
          {
            featureKey: "exertionRecovery",
            labelKey: "admin.createOrg.featureLabels.exertionRecovery",
            labelFallback: "Exertion Recovery",
          },
          {
            featureKey: "sixMinuteWalk",
            labelKey: "admin.createOrg.featureLabels.sixMinuteWalk",
            labelFallback: "6 Minute Walk",
          },
          {
            featureKey: "manualAudio",
            labelKey: "admin.createOrg.featureLabels.manualAudio",
            labelFallback: "Manual Audio",
          },
        ],
      },
      {
        sectionKey: "results",
        titleKey: "admin.createOrg.featureSections.results",
        titleFallback: "Results",
        features: [
          {
            featureKey: "rr",
            labelKey: "admin.createOrg.featureLabels.rr",
            labelFallback: "RR",
          },
          {
            featureKey: "hr",
            labelKey: "admin.createOrg.featureLabels.hr",
            labelFallback: "HR",
          },
          {
            featureKey: "hrv",
            labelKey: "admin.createOrg.featureLabels.hrv",
            labelFallback: "HRV",
          },
          {
            featureKey: "tv",
            labelKey: "admin.createOrg.featureLabels.tv",
            labelFallback: "TV",
          },
          {
            featureKey: "fev1",
            labelKey: "admin.createOrg.featureLabels.fev1",
            labelFallback: "FEV1",
          },
          {
            featureKey: "fvc",
            labelKey: "admin.createOrg.featureLabels.fvc",
            labelFallback: "FVC",
          },
          {
            featureKey: "pef",
            labelKey: "admin.createOrg.featureLabels.pef",
            labelFallback: "PEF",
          },
          {
            featureKey: "fev1Fvc",
            labelKey: "admin.createOrg.featureLabels.fev1Fvc",
            labelFallback: "FEV1/FVC",
          },
          {
            featureKey: "flowLoops",
            labelKey: "admin.createOrg.featureLabels.flowLoops",
            labelFallback: "Flow Loops",
          },
          {
            featureKey: "relativeChange",
            labelKey: "admin.createOrg.featureLabels.relativeChange",
            labelFallback: "Relative Change (%)",
          },
          {
            featureKey: "resultsTrends",
            labelKey: "admin.createOrg.featureLabels.resultsTrends",
            labelFallback: "Results Trends",
          },
          {
            featureKey: "cardiacQuality",
            labelKey: "admin.createOrg.featureLabels.cardiacQuality",
            labelFallback: "Cardiac Quality",
          },
          {
            featureKey: "signalQuality",
            labelKey: "admin.createOrg.featureLabels.signalQuality",
            labelFallback: "Signal Quality",
          },
          {
            featureKey: "spirometryQuality",
            labelKey: "admin.createOrg.featureLabels.spirometryQuality",
            labelFallback: "Spirometry Quality",
          },
        ],
      },
      {
        sectionKey: "elements",
        titleKey: "admin.createOrg.featureSections.elements",
        titleFallback: "Elements",
        features: [
          {
            featureKey: "poincareChart",
            labelKey: "admin.createOrg.featureLabels.poincareChart",
            labelFallback: "Poincaré Chart",
          },
          {
            featureKey: "respiratorySignalChart",
            labelKey: "admin.createOrg.featureLabels.respiratorySignalChart",
            labelFallback: "Respiratory Signal Chart",
          },
          {
            featureKey: "spirometrySegmentChart",
            labelKey: "admin.createOrg.featureLabels.spirometrySegmentChart",
            labelFallback: "Spirometry Segment Chart",
          },
        ],
      },
      {
        sectionKey: "misc",
        titleKey: "admin.createOrg.featureSections.misc",
        titleFallback: "Misc",
        features: [
          {
            featureKey: "emailNotification",
            labelKey: "admin.createOrg.featureLabels.emailNotification",
            labelFallback: "Email Notification",
          },
          {
            featureKey: "weatherDetails",
            labelKey: "admin.createOrg.featureLabels.weatherDetails",
            labelFallback: "Weather Details",
          },
        ],
      },
    ],
  },
  {
    surfaceKey: "dashboard",
    titleKey: "admin.createOrg.featureSurfaces.dashboard",
    titleFallback: "Clinician dashboard",
    sections: [
      {
        sectionKey: "results",
        titleKey: "admin.createOrg.featureSections.results",
        titleFallback: "Results",
        features: [
          {
            featureKey: "videoAudioReview",
            labelKey: "admin.createOrg.featureLabels.videoAudioReview",
            labelFallback: "Video/Audio Review",
          },
          {
            featureKey: "spectrograms",
            labelKey: "admin.createOrg.featureLabels.spectrograms",
            labelFallback: "Spectrograms",
          },
          {
            featureKey: "rr",
            labelKey: "admin.createOrg.featureLabels.rr",
            labelFallback: "RR",
          },
          {
            featureKey: "hr",
            labelKey: "admin.createOrg.featureLabels.hr",
            labelFallback: "HR",
          },
          {
            featureKey: "hrv",
            labelKey: "admin.createOrg.featureLabels.hrv",
            labelFallback: "HRV",
          },
          {
            featureKey: "tv",
            labelKey: "admin.createOrg.featureLabels.tv",
            labelFallback: "TV",
          },
          {
            featureKey: "fev1",
            labelKey: "admin.createOrg.featureLabels.fev1",
            labelFallback: "FEV1",
          },
          {
            featureKey: "fvc",
            labelKey: "admin.createOrg.featureLabels.fvc",
            labelFallback: "FVC",
          },
          {
            featureKey: "pef",
            labelKey: "admin.createOrg.featureLabels.pef",
            labelFallback: "PEF",
          },
          {
            featureKey: "fev1Fvc",
            labelKey: "admin.createOrg.featureLabels.fev1Fvc",
            labelFallback: "FEV1/FVC",
          },
          {
            featureKey: "flowLoops",
            labelKey: "admin.createOrg.featureLabels.flowLoops",
            labelFallback: "Flow Loops",
          },
          {
            featureKey: "relativeChange",
            labelKey: "admin.createOrg.featureLabels.relativeChange",
            labelFallback: "Relative Change (%)",
          },
          {
            featureKey: "signalQuality",
            labelKey: "admin.createOrg.featureLabels.signalQuality",
            labelFallback: "Signal Quality",
          },
          {
            featureKey: "spirometryQuality",
            labelKey: "admin.createOrg.featureLabels.spirometryQuality",
            labelFallback: "Spirometry Quality",
          },
          {
            featureKey: "cardiacQuality",
            labelKey: "admin.createOrg.featureLabels.cardiacQuality",
            labelFallback: "Cardiac Quality",
          },
          {
            featureKey: "environmentalContext",
            labelKey: "admin.createOrg.featureLabels.environmentalContext",
            labelFallback: "Environmental Context",
          },
          {
            featureKey: "weatherDetails",
            labelKey: "admin.createOrg.featureLabels.weatherDetails",
            labelFallback: "Weather Details",
          },
          {
            featureKey: "poincareChart",
            labelKey: "admin.createOrg.featureLabels.poincareChart",
            labelFallback: "Poincaré Chart",
          },
          {
            featureKey: "respiratorySignalChart",
            labelKey: "admin.createOrg.featureLabels.respiratorySignalChart",
            labelFallback: "Respiratory Signal Chart",
          },
          {
            featureKey: "spirometrySegmentChart",
            labelKey: "admin.createOrg.featureLabels.spirometrySegmentChart",
            labelFallback: "Spirometry Segment Chart",
          },
          {
            featureKey: "respiratoryRatePerBreathChart",
            labelKey:
              "admin.createOrg.featureLabels.respiratoryRatePerBreathChart",
            labelFallback: "Respiratory Rate per Breath Chart",
          },
          {
            featureKey: "rrPerBreathMatrix",
            labelKey: "admin.createOrg.featureLabels.rrPerBreathMatrix",
            labelFallback: "RR Per Breath Matrix",
          },
          {
            featureKey: "averageFlowVolumeLoopChart",
            labelKey:
              "admin.createOrg.featureLabels.averageFlowVolumeLoopChart",
            labelFallback: "Average Flow-Volume Loop Chart",
          },
          {
            featureKey: "flowVolumeLoopsCharts",
            labelKey: "admin.createOrg.featureLabels.flowVolumeLoopsCharts",
            labelFallback: "Flow-Volume Loops Chart(s)",
          },
          {
            featureKey: "spirometryFlowLoop",
            labelKey: "admin.createOrg.featureLabels.spirometryFlowLoop",
            labelFallback: "Spirometry Flow Loop",
          },
          {
            featureKey: "recoveryDecay",
            labelKey: "admin.createOrg.featureLabels.recoveryDecay",
            labelFallback: "Recovery Decay",
          },
          {
            featureKey: "breathingVariabilityPoincare",
            labelKey:
              "admin.createOrg.featureLabels.breathingVariabilityPoincare",
            labelFallback: "Breathing Variability Poincaré",
          },
          {
            featureKey: "cumulativeBreathMagnitude",
            labelKey: "admin.createOrg.featureLabels.cumulativeBreathMagnitude",
            labelFallback: "Cumulative Breath Magnitude",
          },
          {
            featureKey: "rollingRecoveryFlowVolumeLoops",
            labelKey:
              "admin.createOrg.featureLabels.rollingRecoveryFlowVolumeLoops",
            labelFallback: "Rolling recovery flow-volume loops",
          },
        ],
      },
    ],
  },
];

function buildDefaultOrganisationFeatureAccess(productPresentationProfile) {
  return ORG_PRODUCT_FEATURE_SECTIONS.reduce((surfaceAcc, surface) => {
    surfaceAcc[surface.surfaceKey] = surface.sections.reduce(
      (sectionAcc, section) => {
        sectionAcc[section.sectionKey] = section.features.reduce(
          (featureAcc, feature) => {
            featureAcc[feature.featureKey] = Boolean(
              productPresentationProfile?.[surface.surfaceKey]?.[
                section.sectionKey
              ]?.[feature.featureKey],
            );
            return featureAcc;
          },
          {},
        );
        return sectionAcc;
      },
      {},
    );
    return surfaceAcc;
  }, {});
}

function createDefaultOrganisationForm(productPresentationProfile) {
  return {
    organisationId: "",
    joinSlug: "",
    name: "",
    productPresentationFeatureAccess: buildDefaultOrganisationFeatureAccess(
      productPresentationProfile,
    ),
  };
}

function createManageOrganisationForm(
  organisation,
  productPresentationProfile,
) {
  return {
    organisationId: organisation?.id || "",
    joinSlug: organisation?.joinSlug || "",
    name: organisation?.name || "",
    productPresentationFeatureAccess:
      organisation?.productPresentationFeatureAccess ||
      buildDefaultOrganisationFeatureAccess(productPresentationProfile),
  };
}

function resolveFeatureAvailability(
  productPresentationProfile,
  surfaceKey,
  sectionKey,
  featureKey,
) {
  return Boolean(
    productPresentationProfile?.[surfaceKey]?.[sectionKey]?.[featureKey],
  );
}

function resolveFeatureAccessValue(
  productPresentationFeatureAccess,
  productPresentationProfile,
  surfaceKey,
  sectionKey,
  featureKey,
) {
  const explicitValue =
    productPresentationFeatureAccess?.[surfaceKey]?.[sectionKey]?.[featureKey];

  if (typeof explicitValue === "boolean") {
    return explicitValue;
  }

  return resolveFeatureAvailability(
    productPresentationProfile,
    surfaceKey,
    sectionKey,
    featureKey,
  );
}

function toggleOrganisationFeatureAccess(
  previousFeatureAccess,
  surfaceKey,
  sectionKey,
  featureKey,
  checked,
) {
  return {
    ...(previousFeatureAccess || {}),
    [surfaceKey]: {
      ...(previousFeatureAccess?.[surfaceKey] || {}),
      [sectionKey]: {
        ...(previousFeatureAccess?.[surfaceKey]?.[sectionKey] || {}),
        [featureKey]: checked,
      },
    },
  };
}

function OrganisationProfileFeatureMatrix({
  t,
  productPresentationProfile,
  productPresentationFeatureAccess,
  onFeatureAccessChange,
  busy,
}) {
  return (
    <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto pr-1">
      {ORG_PRODUCT_FEATURE_SECTIONS.map((surface) => (
        <section
          key={surface.surfaceKey}
          className="rounded-xl border border-greyscale-1 p-4"
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <h3 className="text-lg font-semibold text-typography">
                {t(surface.titleKey, surface.titleFallback)}
              </h3>
            </div>

            {surface.sections.map((section) => (
              <div key={`${surface.surfaceKey}-${section.sectionKey}`}>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-greyscale-3">
                  {t(section.titleKey, section.titleFallback)}
                </div>

                <div className="flex flex-col gap-2">
                  {section.features.map((feature) => {
                    const available = resolveFeatureAvailability(
                      productPresentationProfile,
                      surface.surfaceKey,
                      section.sectionKey,
                      feature.featureKey,
                    );
                    const checked = resolveFeatureAccessValue(
                      productPresentationFeatureAccess,
                      productPresentationProfile,
                      surface.surfaceKey,
                      section.sectionKey,
                      feature.featureKey,
                    );

                    return (
                      <label
                        key={feature.featureKey}
                        className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 ${
                          available
                            ? "border-greyscale-1"
                            : "border-greyscale-1 opacity-60"
                        }`}
                      >
                        <div className="flex flex-col gap-1">
                          <span className="text-sm font-medium text-typography">
                            {t(feature.labelKey, feature.labelFallback)}
                          </span>
                          {!available ? (
                            <span className="text-xs text-greyscale-3">
                              {t(
                                "admin.createOrg.lockedByDeployment",
                                "Not available in this deployment.",
                              )}
                            </span>
                          ) : null}
                        </div>

                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-primary"
                          checked={checked}
                          onChange={onFeatureAccessChange(
                            surface.surfaceKey,
                            section.sectionKey,
                            feature.featureKey,
                          )}
                          disabled={!available || busy}
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/** Pulls the deterministic backend error message, falling back to a default. */
function backendError(error, fallback) {
  return error?.response?.data?.error?.message || fallback;
}

function formatRoleLabel(roleValue, t) {
  const match = ROLE_OPTIONS.find((role) => role.value === roleValue);
  return match ? t(match.key, match.fallback) : roleValue || "-";
}

function formatInviteStatus(status, t) {
  switch (status) {
    case "redeemed":
      return t("admin.invite.statusRedeemed", "Redeemed");
    case "expired":
      return t("admin.invite.statusExpired", "Expired");
    default:
      return t("admin.invite.statusPending", "Pending");
  }
}

function inviteStatusClassName(status) {
  switch (status) {
    case "redeemed":
      return "bg-green-100 text-green-800 border-green-200";
    case "expired":
      return "bg-red-100 text-red-800 border-red-200";
    default:
      return "bg-greyscale-1 text-typography border-greyscale-1";
  }
}

function formatOrganisationStatus(status) {
  if (!status) {
    return "-";
  }

  return `${String(status).charAt(0).toUpperCase()}${String(status).slice(1)}`;
}

function formatInviteExpiry(invite, t) {
  if (invite?.status === "redeemed") {
    return t("admin.members.redeemed", "Redeemed");
  }

  return invite?.expiresAt
    ? new Date(invite.expiresAt).toLocaleDateString()
    : "-";
}

function getInviteActionKey(invite) {
  return invite?.code || invite?.memberUid || null;
}

function deriveJoinSlugCandidate(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/gu, "")
    .slice(0, 4);
}

function canEditRedeemedMembershipRole(invite) {
  return (
    invite?.status === "redeemed" &&
    Boolean(invite?.memberUid) &&
    invite?.organisationRole !== "admin"
  );
}

function canEditRoleRecord(invite) {
  return Boolean(invite?.code) || canEditRedeemedMembershipRole(invite);
}

/**
 * Clears short-lived notices after a fixed delay so inline status messages do
 * not linger once the user has seen them.
 *
 * Access codes intentionally stay visible until the user takes another action,
 * so this helper is only used for plain text notices.
 */
function useTransientNotice(value, setValue, delayMs = TRANSIENT_NOTICE_MS) {
  useEffect(() => {
    if (!value) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setValue(null);
    }, delayMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [delayMs, setValue, value]);
}

export default function OrganisationAdminPanel() {
  const { t } = useTranslation();
  const {
    isBootstrapAdmin,
    organisation,
    accountContext: userContext,
  } = useAuth();
  const accountContext = userContext?.accountContext ?? null;
  const deploymentProductPresentationProfile =
    userContext?.deploymentProductPresentationProfile ||
    userContext?.productPresentationProfile ||
    null;
  const ownOrgId = organisation?.id || accountContext?.organisationId || "";
  const canManageOrganisation = canManageDashboardOrganisation(userContext, {
    isBootstrapAdmin,
  });
  const canConfigureOrganisationPresentation = Boolean(
    deploymentProductPresentationProfile?.id &&
    deploymentProductPresentationProfile?.available !== false,
  );

  const [orgForm, setOrgForm] = useState(() =>
    createDefaultOrganisationForm(deploymentProductPresentationProfile),
  );
  const [orgBusy, setOrgBusy] = useState(false);
  const [orgResult, setOrgResult] = useState(null);
  const [orgError, setOrgError] = useState(null);
  const [orgJoinSlugDirty, setOrgJoinSlugDirty] = useState(false);
  const [createOrgModalOpen, setCreateOrgModalOpen] = useState(false);
  const [manageOrgModalOpen, setManageOrgModalOpen] = useState(false);
  const [managedOrganisation, setManagedOrganisation] = useState(null);
  const [manageOrgForm, setManageOrgForm] = useState(() =>
    createManageOrganisationForm(null, deploymentProductPresentationProfile),
  );
  const [manageOrgBusy, setManageOrgBusy] = useState(false);
  const [manageOrgResult, setManageOrgResult] = useState(null);
  const [manageOrgError, setManageOrgError] = useState(null);
  const [organisations, setOrganisations] = useState([]);
  const [organisationsLoading, setOrganisationsLoading] = useState(false);
  const [organisationsError, setOrganisationsError] = useState(null);
  const [selectedOrganisationId, setSelectedOrganisationId] = useState("");

  const [inviteForm, setInviteForm] = useState({
    email: "",
    organisationId: "",
    organisationRole: "clinician",
  });
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteError, setInviteError] = useState(null);
  const [inviteList, setInviteList] = useState([]);
  const [inviteListLoading, setInviteListLoading] = useState(false);
  const [inviteListError, setInviteListError] = useState(null);
  const [inviteActionBusyCode, setInviteActionBusyCode] = useState(null);
  const [inviteActionError, setInviteActionError] = useState(null);
  const [inviteActionMessage, setInviteActionMessage] = useState(null);
  const [roleEditorInvite, setRoleEditorInvite] = useState(null);
  const [roleEditorValue, setRoleEditorValue] = useState("clinician");
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const inviteListScopeOrganisationId = isBootstrapAdmin
    ? selectedOrganisationId
    : ownOrgId;
  const canReviewMembers =
    isBootstrapAdmin || (Boolean(ownOrgId) && canManageOrganisation);
  const resetOrgForm = useCallback(() => {
    setOrgForm(
      createDefaultOrganisationForm(deploymentProductPresentationProfile),
    );
    setOrgJoinSlugDirty(false);
  }, [deploymentProductPresentationProfile]);

  const onOrgIdChange = (e) => {
    const nextOrganisationId = e.target.value;

    setOrgForm((prev) => {
      const previousDerived = deriveJoinSlugCandidate(prev.organisationId);
      const shouldSyncJoinSlug =
        !orgJoinSlugDirty ||
        !prev.joinSlug ||
        prev.joinSlug === previousDerived;

      return {
        ...prev,
        organisationId: nextOrganisationId,
        joinSlug: shouldSyncJoinSlug
          ? deriveJoinSlugCandidate(nextOrganisationId)
          : prev.joinSlug,
      };
    });
  };
  const onOrgJoinSlugChange = (e) => {
    setOrgJoinSlugDirty(true);
    setOrgForm((prev) => ({
      ...prev,
      joinSlug: deriveJoinSlugCandidate(e.target.value),
    }));
  };
  const onOrgChange = (key) => (e) =>
    setOrgForm((prev) => ({ ...prev, [key]: e.target.value }));
  const onOrgFeatureAccessChange = useCallback(
    (surfaceKey, sectionKey, featureKey) => (event) => {
      const checked = event.target.checked;

      setOrgForm((previous) => ({
        ...previous,
        productPresentationFeatureAccess: toggleOrganisationFeatureAccess(
          previous.productPresentationFeatureAccess,
          surfaceKey,
          sectionKey,
          featureKey,
          checked,
        ),
      }));
    },
    [],
  );
  const onManageOrgFeatureAccessChange = useCallback(
    (surfaceKey, sectionKey, featureKey) => (event) => {
      const checked = event.target.checked;

      setManageOrgForm((previous) => ({
        ...previous,
        productPresentationFeatureAccess: toggleOrganisationFeatureAccess(
          previous.productPresentationFeatureAccess,
          surfaceKey,
          sectionKey,
          featureKey,
          checked,
        ),
      }));
    },
    [],
  );
  const onInviteChange = (key) => (e) =>
    setInviteForm((prev) => ({ ...prev, [key]: e.target.value }));
  const patchInviteListRecord = useCallback((matchesRecord, applyPatch) => {
    setInviteList((previousList) =>
      previousList.map((record) =>
        matchesRecord(record) ? applyPatch(record) : record,
      ),
    );
  }, []);

  useTransientNotice(orgResult, setOrgResult);
  useTransientNotice(manageOrgResult, setManageOrgResult);
  useTransientNotice(inviteActionMessage, setInviteActionMessage);

  const loadOrganisations = useCallback(async () => {
    if (!isBootstrapAdmin) {
      setOrganisations([]);
      setOrganisationsError(null);
      return;
    }

    setOrganisationsLoading(true);
    setOrganisationsError(null);
    try {
      const data = await fetchOrganisations();
      setOrganisations(
        Array.isArray(data?.organisations) ? data.organisations : [],
      );
    } catch (err) {
      setOrganisations([]);
      setOrganisationsError(
        backendError(err, "Couldn’t load organisations right now."),
      );
    } finally {
      setOrganisationsLoading(false);
    }
  }, [isBootstrapAdmin]);

  const loadInviteList = useCallback(async () => {
    if (!canReviewMembers || !inviteListScopeOrganisationId) {
      setInviteList([]);
      setInviteListLoading(false);
      setInviteListError(null);
      return;
    }

    setInviteListLoading(true);
    setInviteListError(null);
    try {
      const data = await fetchInvites({
        organisationId: inviteListScopeOrganisationId,
      });
      setInviteList(Array.isArray(data?.invites) ? data.invites : []);
    } catch (err) {
      setInviteList([]);
      setInviteListError(
        backendError(err, "Couldn’t load organisation invites right now."),
      );
    } finally {
      setInviteListLoading(false);
    }
  }, [canReviewMembers, inviteListScopeOrganisationId]);

  useEffect(() => {
    loadOrganisations();
  }, [loadOrganisations]);

  useEffect(() => {
    loadInviteList();
  }, [loadInviteList]);

  useEffect(() => {
    if (createOrgModalOpen || orgBusy) {
      return;
    }

    resetOrgForm();
  }, [createOrgModalOpen, orgBusy, resetOrgForm]);

  const openCreateOrgModal = useCallback(() => {
    setOrgError(null);
    resetOrgForm();
    setCreateOrgModalOpen(true);
  }, [resetOrgForm]);

  const closeCreateOrgModal = useCallback(() => {
    if (orgBusy) {
      return;
    }

    setCreateOrgModalOpen(false);
  }, [orgBusy]);

  const openManageOrgModal = useCallback(
    (targetOrganisation) => {
      setManageOrgError(null);
      setManagedOrganisation(targetOrganisation);
      setManageOrgForm(
        createManageOrganisationForm(
          targetOrganisation,
          deploymentProductPresentationProfile,
        ),
      );
      setManageOrgModalOpen(true);
    },
    [deploymentProductPresentationProfile],
  );

  const closeManageOrgModal = useCallback(() => {
    if (manageOrgBusy) {
      return;
    }

    setManageOrgModalOpen(false);
  }, [manageOrgBusy]);

  const submitOrg = useCallback(
    async (event) => {
      event.preventDefault();
      setOrgError(null);
      setOrgResult(null);
      setOrgBusy(true);
      try {
        const { organisation: created } = await createOrganisation(orgForm);
        setOrgResult(created);
        resetOrgForm();
        setCreateOrgModalOpen(false);
        setInviteForm((prev) => ({ ...prev, organisationId: created.id }));
        setSelectedOrganisationId(created.id);
        await loadOrganisations();
      } catch (err) {
        setOrgError(
          backendError(
            err,
            t("admin.createOrg.error", "Couldn’t create the organisation."),
          ),
        );
      } finally {
        setOrgBusy(false);
      }
    },
    [loadOrganisations, orgForm, resetOrgForm, t],
  );

  const submitManagedOrganisationProfile = useCallback(
    async (event) => {
      event.preventDefault();

      if (!managedOrganisation?.id) {
        return;
      }

      setManageOrgError(null);
      setManageOrgResult(null);
      setManageOrgBusy(true);

      try {
        const { organisation: updatedOrganisation } =
          await updateOrganisationProfile(managedOrganisation.id, {
            productPresentationFeatureAccess:
              manageOrgForm.productPresentationFeatureAccess,
          });
        setManagedOrganisation(updatedOrganisation);
        setManageOrgForm(
          createManageOrganisationForm(
            updatedOrganisation,
            deploymentProductPresentationProfile,
          ),
        );
        setManageOrgResult(updatedOrganisation);
        setManageOrgModalOpen(false);
        await loadOrganisations();
      } catch (err) {
        setManageOrgError(
          backendError(
            err,
            t(
              "admin.manageOrg.error",
              "Couldn’t update the organisation profile.",
            ),
          ),
        );
      } finally {
        setManageOrgBusy(false);
      }
    },
    [
      deploymentProductPresentationProfile,
      loadOrganisations,
      manageOrgForm.productPresentationFeatureAccess,
      managedOrganisation,
      t,
    ],
  );

  const submitInvite = useCallback(
    async (event) => {
      event.preventDefault();
      setInviteError(null);
      setInviteResult(null);
      setInviteActionError(null);
      setInviteActionMessage(null);
      setInviteBusy(true);
      try {
        const { invite } = await createInvite({
          email: inviteForm.email,
          organisationId: isBootstrapAdmin
            ? inviteForm.organisationId
            : ownOrgId,
          organisationRole: inviteForm.organisationRole,
        });
        setInviteResult(invite);
        setInviteForm((prev) => ({ ...prev, email: "" }));
        await loadInviteList();
      } catch (err) {
        setInviteError(
          backendError(
            err,
            t("admin.invite.error", "Couldn’t create the invite."),
          ),
        );
      } finally {
        setInviteBusy(false);
      }
    },
    [inviteForm, isBootstrapAdmin, loadInviteList, ownOrgId, t],
  );

  const openInviteRoleEditor = useCallback((invite) => {
    if (!canEditRoleRecord(invite)) {
      return;
    }

    setInviteActionError(null);
    setInviteActionMessage(null);
    setRoleEditorInvite(invite);
    setRoleEditorValue(invite.organisationRole || "clinician");
  }, []);

  const closeInviteRoleEditor = useCallback(() => {
    setRoleEditorInvite(null);
    setRoleEditorValue("clinician");
  }, []);

  const patchInviteRecord = useCallback(
    async (invite, payload, successMessage) => {
      if (!invite?.code) {
        return false;
      }

      setInviteActionBusyCode(getInviteActionKey(invite));
      setInviteActionError(null);
      setInviteActionMessage(null);

      try {
        const { invite: updatedInvite } = await updateInvite(
          invite.code,
          payload,
        );
        await loadInviteList();
        if (updatedInvite?.code) {
          patchInviteListRecord(
            (record) => record?.code === updatedInvite.code,
            (record) => ({ ...record, ...updatedInvite }),
          );
        }
        setInviteActionMessage(successMessage);
        return true;
      } catch (err) {
        setInviteActionError(
          backendError(
            err,
            t("admin.invite.updateError", "Couldn’t update the invite."),
          ),
        );
        return false;
      } finally {
        setInviteActionBusyCode(null);
      }
    },
    [loadInviteList, patchInviteListRecord, t],
  );

  const patchMembershipRecord = useCallback(
    async (invite, payload, successMessage) => {
      if (!invite?.memberUid) {
        return false;
      }

      setInviteActionBusyCode(getInviteActionKey(invite));
      setInviteActionError(null);
      setInviteActionMessage(null);

      try {
        const { email, memberUid, membership } = await updateMembershipRole(
          invite.memberUid,
          payload,
        );
        await loadInviteList();
        if (memberUid) {
          patchInviteListRecord(
            (record) => record?.memberUid === memberUid,
            (record) => ({
              ...record,
              ...(email ? { email } : {}),
              ...(membership?.organisationId
                ? { organisationId: membership.organisationId }
                : {}),
              ...(membership?.organisationRole
                ? { organisationRole: membership.organisationRole }
                : {}),
            }),
          );
        }
        setInviteActionMessage(successMessage);
        return true;
      } catch (err) {
        setInviteActionError(
          backendError(
            err,
            t("admin.members.updateError", "Couldn’t update the member role."),
          ),
        );
        return false;
      } finally {
        setInviteActionBusyCode(null);
      }
    },
    [loadInviteList, patchInviteListRecord, t],
  );

  const saveInviteRole = useCallback(async () => {
    if (!roleEditorInvite) {
      return;
    }

    if (roleEditorValue === roleEditorInvite.organisationRole) {
      closeInviteRoleEditor();
      return;
    }

    const saveRole = roleEditorInvite.code
      ? patchInviteRecord
      : patchMembershipRecord;
    const saved = await saveRole(
      roleEditorInvite,
      { organisationRole: roleEditorValue },
      t("admin.invite.roleUpdated", "Updated the role for {{email}}.", {
        email: roleEditorInvite.email,
      }),
    );

    if (saved) {
      closeInviteRoleEditor();
    }
  }, [
    closeInviteRoleEditor,
    patchMembershipRecord,
    patchInviteRecord,
    roleEditorInvite,
    roleEditorValue,
    t,
  ]);

  const extendInviteExpiry = useCallback(
    async (invite) => {
      if (!invite?.code) {
        return;
      }

      await patchInviteRecord(
        invite,
        { extendExpiryDays: 7 },
        t(
          "admin.invite.expiryExtended",
          "Extended the invite for {{email}} by 7 days.",
          {
            email: invite.email,
          },
        ),
      );
    },
    [patchInviteRecord, t],
  );

  const copyCode = useCallback((code) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(code).catch(() => {});
    }
  }, []);

  const openInviteModal = useCallback(() => {
    setInviteError(null);
    setInviteResult(null);
    setInviteModalOpen(true);
  }, []);

  const closeInviteModal = useCallback(() => {
    if (inviteBusy) {
      return;
    }
    setInviteModalOpen(false);
  }, [inviteBusy]);

  const canInvite =
    isBootstrapAdmin || (Boolean(ownOrgId) && canManageOrganisation);
  const isOrgAdminLayout = !isBootstrapAdmin && canReviewMembers;
  const organisationsSummary = useMemo(
    () =>
      organisations.map((item) => ({
        ...item,
        statusLabel: formatOrganisationStatus(item.status),
      })),
    [organisations],
  );

  const selectedOrganisationSummary = useMemo(() => {
    if (!inviteListScopeOrganisationId) {
      return null;
    }

    if (!isBootstrapAdmin) {
      return organisation
        ? {
            id: organisation.id,
            name: organisation.name || organisation.id,
          }
        : {
            id: inviteListScopeOrganisationId,
            name: inviteListScopeOrganisationId,
          };
    }

    const match = organisationsSummary.find(
      (item) => item.id === inviteListScopeOrganisationId,
    );

    return match
      ? {
          id: match.id,
          name: match.name || match.id,
        }
      : {
          id: inviteListScopeOrganisationId,
          name: inviteListScopeOrganisationId,
        };
  }, [
    inviteListScopeOrganisationId,
    isBootstrapAdmin,
    organisation,
    organisationsSummary,
  ]);
  const inviteOrganisationSummary = useMemo(() => {
    if (!isBootstrapAdmin) {
      return organisation
        ? {
            id: organisation.id,
            name: organisation.name || organisation.id,
          }
        : null;
    }

    const normalizedOrganisationId = String(
      inviteForm.organisationId || "",
    ).trim();
    if (!normalizedOrganisationId) {
      return null;
    }

    const match = organisationsSummary.find(
      (item) => item.id === normalizedOrganisationId,
    );

    return match
      ? {
          id: match.id,
          name: match.name || match.id,
        }
      : {
          id: normalizedOrganisationId,
          name: normalizedOrganisationId,
        };
  }, [
    inviteForm.organisationId,
    isBootstrapAdmin,
    organisation,
    organisationsSummary,
  ]);
  const inviteHelp = inviteOrganisationSummary
    ? t("admin.invite.helpNamed", "Issues an access code for {{name}}.", {
        name: inviteOrganisationSummary.name,
      })
    : isBootstrapAdmin
      ? t(
          "admin.invite.helpSuper",
          "Issues an access code tied to an organisation, email, and role.",
        )
      : t(
          "admin.invite.helpGeneric",
          "Issues an access code for your organisation.",
        );

  const inviteMembersSection = canReviewMembers ? (
    <section
      className={`h-full w-full flex flex-col gap-2 ${
        isOrgAdminLayout ? "flex-1 min-h-0 overflow-hidden" : ""
      }`}
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="title-text">
            {selectedOrganisationSummary
              ? t("admin.members.titleNamed", "{{name}} members", {
                  name: selectedOrganisationSummary.name,
                })
              : t("admin.members.title", "Organisation members")}
          </h3>
          <div className="flex gap-4 items-stretch">
            {isBootstrapAdmin ? (
              <div className="flex flex-col gap-2 md:max-w-md">
                <select
                  id="organisation-members-scope"
                  className="custom-input w-fit min-w-52"
                  value={selectedOrganisationId}
                  onChange={(event) => {
                    const nextOrganisationId = event.target.value;
                    setSelectedOrganisationId(nextOrganisationId);
                    setInviteForm((prev) => ({
                      ...prev,
                      organisationId: nextOrganisationId,
                    }));
                  }}
                >
                  <option value="">
                    {t(
                      "admin.members.scopePlaceholder",
                      "Select an organisation",
                    )}
                  </option>
                  {organisationsSummary.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name || item.id}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {canInvite ? (
              <button
                type="button"
                className="small-primary-button"
                onClick={openInviteModal}
              >
                {t("admin.invite.cta", "Invite")}
              </button>
            ) : null}
          </div>
        </div>

        {renderNotice(inviteListError, "error", { role: "alert" })}

        {renderNotice(inviteActionError, "error", { role: "alert" })}

        {renderNotice(inviteActionMessage, "success")}
      </div>

      {!inviteListScopeOrganisationId && isBootstrapAdmin ? (
        <div className="card w-full h-full p-4">
          <p className="muted-text">
            {t(
              "admin.members.scopeEmpty",
              "Select an organisation to view its members.",
            )}
          </p>
        </div>
      ) : (
        <div
          className={`card overflow-x-scroll ${
            isOrgAdminLayout ? "flex-1 min-h-0" : ""
          }`}
        >
          <div className="h-full ">
            <table className="w-full text-sm text-left">
              <thead className="sticky border-b border-greyscale-1 ">
                <tr>
                  <th className="px-4 py-3 font-medium">
                    {t("admin.members.headers.email", "Email")}
                  </th>
                  <th className="px-4 py-3 font-medium">
                    {t("admin.members.headers.role", "Role")}
                  </th>
                  <th className="px-4 py-3 font-medium">
                    {t("admin.members.headers.code", "Redemption code")}
                  </th>
                  <th className="px-4 py-3 font-medium">
                    {t("admin.members.headers.expires", "Expires")}
                  </th>
                  <th className="px-4 py-3 font-medium">
                    {t("admin.members.headers.actions", "Actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {inviteListLoading ? (
                  <tr>
                    <td className="px-4 py-4 text-greyscale-3" colSpan={5}>
                      {t(
                        "admin.members.loading",
                        "Loading organisation members…",
                      )}
                    </td>
                  </tr>
                ) : inviteList.length === 0 ? (
                  <tr>
                    <td className="px-4 py-4 text-greyscale-3" colSpan={5}>
                      {t(
                        "admin.members.empty",
                        "No organisation members found yet.",
                      )}
                    </td>
                  </tr>
                ) : (
                  inviteList.map((invite) => {
                    const canManageInvite = Boolean(invite.code);
                    const canManageRedeemedRole =
                      canEditRedeemedMembershipRole(invite);
                    const isBusy =
                      inviteActionBusyCode === getInviteActionKey(invite);

                    return (
                      <tr
                        key={`${invite.email}-${invite.createdAt || invite.expiresAt}`}
                        className="border-t border-greyscale-1 align-top"
                      >
                        <td className="px-4 py-3 font-medium text-typography">
                          <div className="flex flex-col gap-1">
                            <span>{invite.email || "-"}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {formatRoleLabel(invite.organisationRole, t)}
                        </td>
                        <td className="px-4 py-3">
                          {invite.code ? (
                            <div className="flex flex-col gap-2">
                              <button
                                type="button"
                                onClick={() => copyCode(invite.code)}
                                className="group inline-flex w-fit items-center gap-2 rounded border border-greyscale-1 bg-white px-3 py-1 text-primary transition-colors hover:bg-greyscale-1 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                                aria-label={t(
                                  "admin.invite.copyCode",
                                  "Copy redemption code",
                                )}
                                title={t(
                                  "admin.invite.copyCode",
                                  "Copy redemption code",
                                )}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                  aria-hidden="true"
                                  className="h-4 w-4 shrink-0"
                                >
                                  <path d="M16 1H4a2 2 0 0 0-2 2v14h2V3h12V1Zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2Zm0 16H8V7h11v14Z" />
                                </svg>

                                <code className="font-bold tracking-widest">
                                  {invite.code}
                                </code>
                              </button>
                            </div>
                          ) : (
                            <span
                              className={`inline-flex w-fit items-center rounded-full border px-2 py-1 text-xs font-medium ${inviteStatusClassName(
                                invite.status,
                              )}`}
                            >
                              {formatInviteStatus(invite.status, t)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {formatInviteExpiry(invite, t)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-2">
                            {canManageInvite ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => openInviteRoleEditor(invite)}
                                  className="small-outlined-button"
                                  disabled={isBusy}
                                >
                                  {t("admin.members.editRole", "Edit role")}
                                </button>

                                <button
                                  type="button"
                                  onClick={() => extendInviteExpiry(invite)}
                                  className="small-outlined-button"
                                  disabled={isBusy}
                                >
                                  {isBusy
                                    ? t("admin.members.extending", "Updating…")
                                    : t(
                                        "admin.members.extendExpiry",
                                        "Extend 7 days",
                                      )}
                                </button>
                              </>
                            ) : canManageRedeemedRole ? (
                              <button
                                type="button"
                                onClick={() => openInviteRoleEditor(invite)}
                                className="small-outlined-button"
                                disabled={isBusy}
                              >
                                {t("admin.members.editRole", "Edit role")}
                              </button>
                            ) : (
                              <span className="text-greyscale-3">
                                {t("admin.members.noActions", "-")}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  ) : null;

  return (
    <div
      className={
        // p-2 keeps card shadows from being clipped by the scroll/overflow box
        // (a box-shadow can't escape an overflow ancestor, so we leave room).
        isBootstrapAdmin
          ? "flex flex-wrap gap-4 h-full min-h-0 overflow-y-auto p-2"
          : "flex flex-col gap-4 h-full w-full min-h-0 overflow-hidden p-2"
      }
    >
      {/* ── Create organisation (super users only) ────────────────────── */}
      {isBootstrapAdmin && (
        <section className="card flex w-full flex-col gap-4 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex flex-col gap-1">
              <h2 className="title-text">
                {t("admin.organisations.title", "Organisations")}
              </h2>
              <p className="muted-text">
                {t(
                  "admin.createOrg.help",
                  "Super-admin only. Creates a new organisation in this region.",
                )}
              </p>
            </div>

            <button
              type="button"
              className="small-primary-button"
              onClick={openCreateOrgModal}
              disabled={!canConfigureOrganisationPresentation}
            >
              {t("admin.createOrg.open", "Create organisation")}
            </button>
          </div>

          {renderNotice(organisationsError, "error", { role: "alert" })}
          {renderNotice(
            orgResult
              ? t(
                  "admin.createOrg.success",
                  "Created {{name}} ({{id}}) — status {{status}}.",
                  {
                    name: orgResult.name,
                    id: orgResult.id,
                    status: orgResult.status,
                  },
                )
              : null,
            "success",
          )}
          {renderNotice(
            manageOrgResult
              ? t(
                  "admin.manageOrg.success",
                  "Updated {{name}} ({{id}}) profile access.",
                  {
                    name: manageOrgResult.name,
                    id: manageOrgResult.id,
                  },
                )
              : null,
            "success",
          )}

          <div className="card overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                <tr>
                  <th className="px-4 py-3">
                    {t("admin.organisations.headers.id", "ID")}
                  </th>
                  <th className="px-4 py-3">
                    {t("admin.organisations.headers.name", "Name")}
                  </th>
                  <th className="px-4 py-3">
                    {t("admin.organisations.headers.joinSlug", "Join slug")}
                  </th>
                  <th className="px-4 py-3">
                    {t("admin.organisations.headers.region", "Region")}
                  </th>
                  <th className="px-4 py-3">
                    {t("admin.organisations.headers.status", "Status")}
                  </th>
                  <th className="px-4 py-3">
                    {t("admin.organisations.headers.actions", "Actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="overflow-y-scroll">
                {organisationsLoading ? (
                  <tr>
                    <td className="px-4 py-4 text-greyscale-3" colSpan={6}>
                      {t(
                        "admin.organisations.loading",
                        "Loading organisations…",
                      )}
                    </td>
                  </tr>
                ) : organisationsSummary.length === 0 ? (
                  <tr>
                    <td className="px-4 py-4 text-greyscale-3" colSpan={6}>
                      {t(
                        "admin.organisations.empty",
                        "No organisations found.",
                      )}
                    </td>
                  </tr>
                ) : (
                  organisationsSummary.map((item) => (
                    <tr key={item.id} className="border-t border-greyscale-1">
                      <td className="px-4 py-3 font-medium text-typography">
                        {item.id}
                      </td>
                      <td className="px-4 py-3">{item.name || "-"}</td>
                      <td className="px-4 py-3 uppercase">
                        {item.joinSlug || "-"}
                      </td>
                      <td className="px-4 py-3 uppercase">
                        {item.region || "-"}
                      </td>
                      <td className="px-4 py-3">{item.statusLabel || "-"}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="small-outlined-button"
                            onClick={() => openManageOrgModal(item)}
                            disabled={!canConfigureOrganisationPresentation}
                          >
                            {t(
                              "admin.organisations.manageProfile",
                              "Manage profile",
                            )}
                          </button>
                          <button
                            type="button"
                            className="small-outlined-button"
                            onClick={() => {
                              setSelectedOrganisationId(item.id);
                              setInviteForm((prev) => ({
                                ...prev,
                                organisationId: item.id,
                              }));
                            }}
                          >
                            {t(
                              "admin.organisations.viewMembers",
                              "View members",
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {inviteMembersSection}

      <Modal
        open={createOrgModalOpen}
        onClose={closeCreateOrgModal}
        panelClassName="w-full max-w-6xl"
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="subtitle-text">
              {t("admin.createOrg.title", "Create organisation")}
            </h2>
            <p className="muted-text">
              {canConfigureOrganisationPresentation
                ? t(
                    "admin.createOrg.matrixHelp",
                    "This deployment resolves {{profileName}} automatically. Use the toggles below to restrict which app and dashboard features the organisation can access.",
                    {
                      profileName:
                        deploymentProductPresentationProfile?.name ||
                        deploymentProductPresentationProfile?.id ||
                        t("admin.createOrg.profileUnavailable", "this profile"),
                    },
                  )
                : t(
                    "admin.createOrg.profileUnavailable",
                    "This deployment's product presentation profile is unavailable right now.",
                  )}
            </p>
          </div>

          {!canConfigureOrganisationPresentation ? (
            <p className="muted-text">
              {t(
                "admin.createOrg.profileUnavailable",
                "This deployment's product presentation profile is unavailable right now.",
              )}
            </p>
          ) : (
            <form
              onSubmit={submitOrg}
              className="flex flex-col gap-4"
              noValidate
            >
              <div className="grid gap-4 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)] lg:items-start">
                <div className="flex flex-col gap-4">
                  <div className="rounded-xl border border-greyscale-1 bg-greyscale-1 p-4">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-semibold uppercase tracking-wide text-greyscale-3">
                        {t(
                          "admin.createOrg.profileLabel",
                          "Resolved deployment profile",
                        )}
                      </span>
                      <span className="text-sm font-medium text-typography">
                        {deploymentProductPresentationProfile?.name ||
                          deploymentProductPresentationProfile?.id ||
                          "-"}
                      </span>
                    </div>
                  </div>

                  <input
                    type="text"
                    className="custom-input w-full"
                    placeholder={t(
                      "admin.createOrg.idPlaceholder",
                      "Organisation ID (e.g. electronrx)",
                    )}
                    value={orgForm.organisationId}
                    onChange={onOrgIdChange}
                    disabled={orgBusy}
                    required
                  />
                  <input
                    type="text"
                    className="custom-input w-full"
                    placeholder={t(
                      "admin.createOrg.joinSlugPlaceholder",
                      "Join slug (e.g. erx)",
                    )}
                    value={orgForm.joinSlug}
                    onChange={onOrgJoinSlugChange}
                    disabled={orgBusy}
                    required
                  />
                  <input
                    type="text"
                    className="custom-input w-full"
                    placeholder={t(
                      "admin.createOrg.namePlaceholder",
                      "Organisation name (e.g. electronRx)",
                    )}
                    value={orgForm.name}
                    onChange={onOrgChange("name")}
                    disabled={orgBusy}
                    required
                  />
                </div>

                <OrganisationProfileFeatureMatrix
                  t={t}
                  productPresentationProfile={
                    deploymentProductPresentationProfile
                  }
                  productPresentationFeatureAccess={
                    orgForm.productPresentationFeatureAccess
                  }
                  onFeatureAccessChange={onOrgFeatureAccessChange}
                  busy={orgBusy}
                />
              </div>

              {renderNotice(orgError, "error", { role: "alert" })}

              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  className="outlined-button"
                  onClick={closeCreateOrgModal}
                  disabled={orgBusy}
                >
                  {t("admin.createOrg.close", "Close")}
                </button>

                <button
                  type="submit"
                  className="primary-button"
                  disabled={orgBusy}
                >
                  {orgBusy
                    ? t("admin.createOrg.submitting", "Creating…")
                    : t("admin.createOrg.submit", "Save and create")}
                </button>
              </div>
            </form>
          )}
        </div>
      </Modal>

      <Modal
        open={manageOrgModalOpen}
        onClose={closeManageOrgModal}
        panelClassName="w-full max-w-6xl"
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="subtitle-text">
              {t("admin.manageOrg.title", "Manage organisation profile")}
            </h2>
            <p className="muted-text">
              {canConfigureOrganisationPresentation
                ? t(
                    "admin.manageOrg.matrixHelp",
                    "This deployment resolves {{profileName}} automatically. Use the toggles below to update which app and dashboard features {{name}} can access.",
                    {
                      profileName:
                        deploymentProductPresentationProfile?.name ||
                        deploymentProductPresentationProfile?.id ||
                        t("admin.createOrg.profileUnavailable", "this profile"),
                      name:
                        managedOrganisation?.name ||
                        managedOrganisation?.id ||
                        t(
                          "admin.manageOrg.organisationLabel",
                          "this organisation",
                        ),
                    },
                  )
                : t(
                    "admin.createOrg.profileUnavailable",
                    "This deployment's product presentation profile is unavailable right now.",
                  )}
            </p>
          </div>

          {!canConfigureOrganisationPresentation ? (
            <p className="muted-text">
              {t(
                "admin.createOrg.profileUnavailable",
                "This deployment's product presentation profile is unavailable right now.",
              )}
            </p>
          ) : (
            <form
              onSubmit={submitManagedOrganisationProfile}
              className="flex flex-col gap-4"
              noValidate
            >
              <div className="grid gap-4 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)] lg:items-start">
                <div className="flex flex-col gap-4">
                  <div className="rounded-xl border border-greyscale-1 bg-greyscale-1 p-4">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-semibold uppercase tracking-wide text-greyscale-3">
                        {t("admin.manageOrg.organisationLabel", "Organisation")}
                      </span>
                      <span className="text-sm font-medium text-typography">
                        {manageOrgForm.name ||
                          manageOrgForm.organisationId ||
                          "-"}
                      </span>
                      <span className="text-xs text-greyscale-3">
                        {manageOrgForm.organisationId || "-"}
                      </span>
                    </div>
                  </div>

                  <div className="rounded-xl border border-greyscale-1 bg-greyscale-1 p-4">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-semibold uppercase tracking-wide text-greyscale-3">
                        {t(
                          "admin.createOrg.profileLabel",
                          "Resolved deployment profile",
                        )}
                      </span>
                      <span className="text-sm font-medium text-typography">
                        {deploymentProductPresentationProfile?.name ||
                          deploymentProductPresentationProfile?.id ||
                          "-"}
                      </span>
                    </div>
                  </div>

                  <div className="rounded-xl border border-greyscale-1 p-4">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-semibold uppercase tracking-wide text-greyscale-3">
                        {t("admin.manageOrg.joinSlugLabel", "Join slug")}
                      </span>
                      <span className="text-sm font-medium uppercase text-typography">
                        {manageOrgForm.joinSlug || "-"}
                      </span>
                    </div>
                  </div>
                </div>

                <OrganisationProfileFeatureMatrix
                  t={t}
                  productPresentationProfile={
                    deploymentProductPresentationProfile
                  }
                  productPresentationFeatureAccess={
                    manageOrgForm.productPresentationFeatureAccess
                  }
                  onFeatureAccessChange={onManageOrgFeatureAccessChange}
                  busy={manageOrgBusy}
                />
              </div>

              {renderNotice(manageOrgError, "error", { role: "alert" })}

              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  className="outlined-button"
                  onClick={closeManageOrgModal}
                  disabled={manageOrgBusy}
                >
                  {t("admin.createOrg.close", "Close")}
                </button>

                <button
                  type="submit"
                  className="primary-button"
                  disabled={manageOrgBusy}
                >
                  {manageOrgBusy
                    ? t("admin.manageOrg.submitting", "Saving…")
                    : t("admin.manageOrg.submit", "Save profile")}
                </button>
              </div>
            </form>
          )}
        </div>
      </Modal>

      {/* ── Invite someone to an organisation (overlay) ───────────────── */}
      <Modal open={inviteModalOpen} onClose={closeInviteModal}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="subtitle-text">
              {inviteOrganisationSummary
                ? t("admin.invite.titleNamed", "Invite to {{name}}", {
                    name: inviteOrganisationSummary.name,
                  })
                : t("admin.invite.title", "Invite to a organisation")}
            </h2>
            <p className="muted-text">{inviteHelp}</p>
          </div>

          {!canInvite ? (
            <p className="muted-text">
              {t(
                "admin.invite.notLinked",
                "You are not yet linked to an organisation.",
              )}
            </p>
          ) : (
            <form
              onSubmit={submitInvite}
              className="flex flex-col gap-4"
              noValidate
            >
              <input
                type="email"
                className="custom-input w-full"
                placeholder={t(
                  "admin.invite.emailPlaceholder",
                  "Invitee email",
                )}
                value={inviteForm.email}
                onChange={onInviteChange("email")}
                disabled={inviteBusy}
                required
              />
              {isBootstrapAdmin && (
                <input
                  type="text"
                  className="custom-input w-full"
                  placeholder={t(
                    "admin.invite.orgIdPlaceholder",
                    "Organisation ID",
                  )}
                  value={inviteForm.organisationId}
                  onChange={onInviteChange("organisationId")}
                  disabled={inviteBusy}
                  required
                />
              )}
              <select
                className="custom-input w-full"
                value={inviteForm.organisationRole}
                onChange={onInviteChange("organisationRole")}
                disabled={inviteBusy}
              >
                {ROLE_OPTIONS.map((role) => (
                  <option key={role.value} value={role.value}>
                    {t(role.key, role.fallback)}
                  </option>
                ))}
              </select>

              {renderNotice(inviteError, "error", { role: "alert" })}

              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  className="outlined-button"
                  onClick={closeInviteModal}
                  disabled={inviteBusy}
                >
                  {t("admin.invite.close", "Close")}
                </button>

                <button
                  type="submit"
                  className="primary-button"
                  disabled={inviteBusy}
                >
                  {inviteBusy
                    ? t("admin.invite.submitting", "Creating…")
                    : t("admin.invite.submit", "Create invite")}
                </button>
              </div>
            </form>
          )}

          {inviteResult && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-greyscale-1 p-3">
              <div className="flex flex-row items-center justify-start gap-2">
                <span className="h-3 w-3 rounded-full bg-green-700"></span>
                <p>
                  {t(
                    "admin.invite.successIntro",
                    "Invite for {{email}} created. Share this access code:",
                    {
                      email: inviteResult.email,
                    },
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={() => copyCode(inviteResult.code)}
                className="group inline-flex w-fit items-center gap-2 rounded bg-transparent text-primary transition-colors hover:bg-greyscale-1 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                aria-label={t("admin.invite.copyCode", "Copy access code")}
                title={t("admin.invite.copyCode", "Copy access code")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0"
                >
                  <path d="M16 1H4a2 2 0 0 0-2 2v14h2V3h12V1Zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2Zm0 16H8V7h11v14Z" />
                </svg>

                <code className="font-bold tracking-widest">
                  {inviteResult.code}
                </code>
              </button>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={Boolean(roleEditorInvite)}
        onClose={inviteActionBusyCode ? undefined : closeInviteRoleEditor}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h3 className="text-lg font-semibold">
              {t("admin.members.roleEditor.title", "Edit member role")}
            </h3>
            <p className="muted-text">
              {t(
                "admin.members.roleEditor.help",
                "Choose the organisation role this user should have.",
              )}
            </p>
            {roleEditorInvite?.email ? (
              <p className="text-sm text-typography">
                {roleEditorInvite.email}
              </p>
            ) : null}
          </div>

          <select
            className="custom-input w-full"
            value={roleEditorValue}
            onChange={(event) => setRoleEditorValue(event.target.value)}
            disabled={Boolean(inviteActionBusyCode)}
          >
            {ROLE_OPTIONS.map((role) => (
              <option key={role.value} value={role.value}>
                {t(role.key, role.fallback)}
              </option>
            ))}
          </select>

          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={closeInviteRoleEditor}
              className="small-outlined-button"
              disabled={Boolean(inviteActionBusyCode)}
            >
              {t("admin.members.cancel", "Cancel")}
            </button>
            <button
              type="button"
              onClick={saveInviteRole}
              className="small-primary-button"
              disabled={Boolean(inviteActionBusyCode)}
            >
              {inviteActionBusyCode
                ? t("admin.members.saving", "Saving…")
                : t("admin.members.saveRole", "Save role")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
